import React, {useId, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, Dialog, createSlotRecipeContext, VisuallyHidden} from '@chakra-ui/react3'
import {FormattedMessage, useIntl} from 'react-intl'
import Slider from '../../../../../leap/components/cms/slider'
import {ArrowShrinkIcon, CenterFocusIcon} from '../../../../../leap/components/icons'
import ZoomImage from '../../../../../leap/components/zoomImage/zoomImage'
import XpdpConfiguratorGalleryItem from '../gallery-item'
import {zoomOverlayRecipe} from './internal.recipe'

const {StylesProvider, useRecipeResult, useStyles} = createSlotRecipeContext({
    recipe: zoomOverlayRecipe
})
const EMPTY_RECIPE_PROPS = {}

const XpdpGalleryZoomOverlayContent = ({
    items,
    initialIndex,
    currentIndex,
    onIndexChange,
    zoomImperativeRef
}) => {
    const {formatMessage} = useIntl()
    const styles = useStyles()
    const sliderId = useId()
    const sliderRef = useRef()

    return (
        <Slider
            ref={sliderRef}
            initialIndex={initialIndex}
            navigationButtonSize="sm"
            variant="configuratorGallery"
            id={sliderId}
            sliderLabel={formatMessage({
                id: 'configurator.gallery.zoom_overlay.slider_label',
                defaultMessage: 'Zoomed gallery'
            })}
            onIndexChange={onIndexChange}
        >
            {items.map((item, index) => (
                <ZoomImage
                    key={index}
                    imageName={item.product?.name}
                    css={styles.zoomFrame}
                    zoomImperativeRef={index === currentIndex ? zoomImperativeRef : undefined}
                >
                    <XpdpConfiguratorGalleryItem
                        product={item.product}
                        stack={item.stack}
                        isExpanded
                    />
                </ZoomImage>
            ))}
        </Slider>
    )
}

XpdpGalleryZoomOverlayContent.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            product: PropTypes.object,
            stack: PropTypes.object
        })
    ),
    initialIndex: PropTypes.number,
    currentIndex: PropTypes.number,
    onIndexChange: PropTypes.func,
    zoomImperativeRef: PropTypes.shape({current: PropTypes.any})
}

const XpdpGalleryZoomOverlay = ({
    open,
    onOpenChange,
    onExitComplete,
    items = [],
    initialIndex = 0,
    finalFocusEl
}) => {
    const {formatMessage} = useIntl()
    const {styles} = useRecipeResult(EMPTY_RECIPE_PROPS)
    const [currentIndex, setCurrentIndex] = useState(initialIndex)
    const zoomImperativeRef = useRef(null)
    const zoomOutButtonRef = useRef(null)

    return (
        <StylesProvider value={styles}>
            <Dialog.Root
                placement="top"
                size="full"
                // avoid default Chakra Dialog's content animation avoding fading effect
                motionPreset="none"
                scrollBehavior="outside"
                initialFocusEl={() => zoomOutButtonRef.current}
                finalFocusEl={finalFocusEl}
                open={open}
                onOpenChange={onOpenChange}
                onExitComplete={onExitComplete}
            >
                <Dialog.Positioner>
                    <Dialog.Content css={styles.content}>
                        <Dialog.Title>
                            <VisuallyHidden>
                                <FormattedMessage
                                    id="configurator.gallery.zoom_overlay.title"
                                    defaultMessage="Zoomed product gallery"
                                />
                            </VisuallyHidden>
                        </Dialog.Title>
                        <Dialog.Body css={styles.body}>
                            <XpdpGalleryZoomOverlayContent
                                items={items}
                                initialIndex={initialIndex}
                                currentIndex={currentIndex}
                                onIndexChange={setCurrentIndex}
                                zoomImperativeRef={zoomImperativeRef}
                            />
                        </Dialog.Body>
                        <Dialog.Footer css={styles.actions}>
                            <Button
                                variant="mediaFilled"
                                size="md"
                                aria-label={formatMessage({
                                    id: 'configurator.gallery.button.center_focus',
                                    defaultMessage: 'Center gallery item'
                                })}
                                onClick={() => zoomImperativeRef.current?.reset?.()}
                            >
                                <CenterFocusIcon aria-hidden="true" />
                            </Button>
                            <Button
                                ref={zoomOutButtonRef}
                                variant="mediaFilled"
                                size="md"
                                aria-label={formatMessage({
                                    id: 'configurator.gallery.button.shrink',
                                    defaultMessage: 'Shrink gallery item'
                                })}
                                onClick={() => onOpenChange?.({open: false})}
                            >
                                <ArrowShrinkIcon aria-hidden="true" />
                            </Button>
                        </Dialog.Footer>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Dialog.Root>
        </StylesProvider>
    )
}

XpdpGalleryZoomOverlay.propTypes = {
    open: PropTypes.bool,
    onOpenChange: PropTypes.func,
    onExitComplete: PropTypes.func,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            product: PropTypes.object,
            stack: PropTypes.object
        })
    ),
    initialIndex: PropTypes.number,
    finalFocusEl: PropTypes.func
}

export default XpdpGalleryZoomOverlay
